import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { FilePdf, PlusCircle } from '@phosphor-icons/react'
import { Button, useToast } from '@/ui'
import { errorMessage } from '../../api/core'
import { useDispatch } from '../../api/recall'
import type { DispatchRow } from '../../model/recall/mappers'
import { ActionButton } from '../../components/Can'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { Check, Code, Facts, Kg, RefLink, Section, StatTile, Status, When } from '../../components/bits'
import { DispatchCorrectionDialog } from '../../components/recall/DispatchCorrectionDialog'
import { openFile } from '../../lib/download'
import { dispatchStatusLabel } from '../../model/recall/words'

/** One dispatch — F7.8: what left for which customer, on which vehicle. */
export function DispatchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useDispatch(id)
  const d = q.data
  const toast = useToast()
  const [printing, setPrinting] = useState(false)
  const [correcting, setCorrecting] = useState(false)

  const printNote = async () => {
    if (!d) return
    setPrinting(true)
    try {
      await openFile(`/dispatches/${d.id}/dispatch-note.pdf`, `dispatch-${d.code}.pdf`)
    } catch (e) {
      toast.error('Could not open the dispatch note', errorMessage(e))
    } finally {
      setPrinting(false)
    }
  }

  return (
    <ConsolePage
      back={{ to: '/c/dispatches', label: 'Dispatches' }}
      eyebrow="Dispatch"
      title={d ? <Code className="text-[inherit]">{d.code}</Code> : 'Dispatch'}
      description={d ? `${d.fromLocation ?? 'Packing hall'} → ${d.toOrg ?? 'customer'}` : undefined}
      actions={
        d ? (
          <>
            <Status status={d.status} label={dispatchStatusLabel(d.status)} />
            <Button variant="secondary" size="sm" leadingIcon={<FilePdf />} loading={printing} onClick={() => void printNote()}>
              Dispatch note
            </Button>
            <ActionButton perm="pack-lots:dispatch" size="sm" variant="secondary" leadingIcon={<PlusCircle />} onClick={() => setCorrecting(true)}>
              Add a missed item
            </ActionButton>
          </>
        ) : undefined
      }
    >
      <QueryState query={q} label="this dispatch">
        {d && <DispatchBody d={d} />}
      </QueryState>
      {d && <DispatchCorrectionDialog dispatch={d} open={correcting} onClose={() => setCorrecting(false)} />}
    </ConsolePage>
  )
}

function DispatchBody({ d }: { d: DispatchRow }) {
  const totalG = d.items.reduce((s, i) => s + (i.netG ?? 0) * i.pouches, 0)
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatTile label="Pouches" value={d.pouches} />
        <StatTile label="Pack lots" value={d.packLots} />
        <StatTile label="Product" value={<Kg grams={totalG} className="text-[inherit]" />} hint="Pouch weight × pouches" />
      </div>
      <DetailColumns
        main={
          <Section title="On the vehicle" eyebrow="Items" bodyClassName="p-0">
            {d.items.length === 0 ? (
              <p className="px-4 py-3 text-[14px] text-muted">No items on this trip.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[14px]">
                  <caption className="sr-only">Items</caption>
                  <thead>
                    <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                      <th scope="col" className="px-4 py-2">Pack lot</th>
                      <th scope="col" className="px-2 py-2">Product</th>
                      <th scope="col" className="px-2 py-2 text-right">Pouches</th>
                      <th scope="col" className="hidden px-2 py-2 text-right sm:table-cell">Kilos</th>
                      <th scope="col" className="hidden px-4 py-2 md:table-cell">Loaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((i) => (
                      <tr key={i.itemId} className="border-t border-rule/60">
                        <td className="px-4 py-2">
                          <RefLink to={`/c/pack-lots/${i.packLotId}`}>{i.packLotCode}</RefLink>
                        </td>
                        <td className="px-2 py-2">{i.skuName ?? '—'}</td>
                        <td className="px-2 py-2 text-right font-mono tabular">{i.pouches}</td>
                        <td className="hidden px-2 py-2 text-right sm:table-cell">
                          <Kg grams={i.netG !== null ? i.netG * i.pouches : null} />
                        </td>
                        <td className="hidden px-4 py-2 md:table-cell">
                          <When at={i.loadedAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-rule font-semibold">
                      <td className="px-4 py-2" colSpan={2}>
                        Total
                      </td>
                      <td className="px-2 py-2 text-right font-mono tabular">{d.pouches}</td>
                      <td className="hidden px-2 py-2 text-right sm:table-cell">
                        <Kg grams={totalG} />
                      </td>
                      <td className="hidden md:table-cell" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>
        }
        side={
          <Section title="Trip">
            <Facts
              columns={2}
              items={[
                { label: 'Customer', value: d.toOrg },
                { label: 'Invoice / challan', value: d.challanNo, mono: true },
                { label: 'Vehicle', value: d.vehicleNo, mono: true },
                { label: 'Vehicle clean', value: <Check ok={d.vehicleClean} label={d.vehicleClean ? 'Yes' : 'No'} /> },
                { label: 'Driver', value: d.driverName },
                { label: 'Driver phone', value: d.driverPhone, mono: true },
                { label: 'From', value: d.fromLocation },
                { label: 'Left', value: <When at={d.dispatchedAt} /> },
              ]}
            />
          </Section>
        }
      />
    </>
  )
}
